import { KeyboardArrowDown } from "@mui/icons-material";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select, { MenuProps, OptionProps } from "react-select";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  activeViewIdAtomFamily,
  defaultViewSF,
  IDropdownItem,
  PLURAL_KEY,
  useIsMobile,
  viewChangesApplyingAtom,
} from "@ryde/shared";

import { MuiButton } from "../..";
import { useViewDropdown } from "./use-view-dropdown";
import { ViewDropdownMenu } from "./view-dropdown-menu";
import {
  ViewDropdownControl,
  ViewDropdownWrapper,
} from "./view-dropdown.elements";
import { ViewDropdownOption } from "./view-dropdown.option";
import { viewDropdownSelectStyles } from "./view-dropdown.select-styles";

export const ViewDropdown: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();

  const {
    onViewDropdownChange,
    handleUpdateView,
    handleDeleteView,
    isOpen,
    handleToggle,
    handleClose,
    mappedViews,
    editViewHandler,
  } = useViewDropdown();
  const { entity } = usePageContext();

  const defaultView = useRecoilValue(defaultViewSF(entity));
  const activeViewId = useRecoilValue(activeViewIdAtomFamily(entity));
  const isUpdating = useRecoilValue(viewChangesApplyingAtom);
  const activeViewDropdownValue: IDropdownItem | undefined = useMemo(() => {
    if (!activeViewId) {
      return undefined;
    }

    return mappedViews.find(x => String(x.value) === String(activeViewId));
  }, [activeViewId, mappedViews]);

  const selectStylesComputed = useMemo(() => {
    return viewDropdownSelectStyles(isMobile);
  }, [isMobile]);

  return (
    <ViewDropdownWrapper
      isOpen={isOpen && !isUpdating}
      onClose={handleClose}
      target={
        <MuiButton onClick={handleToggle} e2eData="views-button">
          {t(`view.${PLURAL_KEY}`)}
          <KeyboardArrowDown data-e2e="svg-arrow-down" />
        </MuiButton>
      }
    >
      <Select
        value={activeViewDropdownValue}
        // eslint-disable-next-line jsx-a11y-x/no-autofocus -- intentional: focus the field when opened by user action
        autoFocus
        backspaceRemovesValue={false}
        data-e2e="views-select"
        components={{
          DropdownIndicator: null,
          IndicatorSeparator: null,
          Control: ViewDropdownControl,
          Menu: menuProps => <ViewDropdownMenu {...(menuProps as MenuProps)} />,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Option: (props: OptionProps) => {
            return (
              <ViewDropdownOption
                {...props}
                handleDelete={handleDeleteView}
                handleUpdate={handleUpdateView}
                activeViewId={activeViewId}
                defaultViewId={defaultView?.id}
                editViewHandler={editViewHandler}
              />
            );
          },
        }}
        controlShouldRenderValue={false}
        isClearable
        menuIsOpen
        options={mappedViews}
        onChange={onViewDropdownChange}
        placeholder={t(`view.formLabel.search`)}
        noOptionsMessage={() => t(`errors.noOptionsMessage`)}
        styles={selectStylesComputed}
        tabSelectsValue={false}
      />
    </ViewDropdownWrapper>
  );
};
